/**
 * OrganizationDashboard
 * Dashboard med kort-layout för organisationsrelaterade funktioner
 */

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/store/authStore';
import { resolveBackTo } from '@/utils/navigationBack';
import ModuleFavoriteStar from '@/components/common/ModuleFavoriteStar';
import {
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const OrganizationDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = resolveBackTo(location.state, ROUTES.SYSTEM_DEQUE);
  const { user } = useAuth();
  const { hasFeatureAccess } = usePermissions();

  const isAdmin = user?.is_global_admin === true;
  const canSee = (code: string) => isAdmin || hasFeatureAccess(code);

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(backTo)}
          className="p-2 hover:bg-bgElevated rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <BuildingOfficeIcon className="w-6 h-6 text-primary" />
          {t('nav.organization') || 'Organisation'}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {canSee('ORGANIZATION') && (
          <Link
            to={ROUTES.ORGANIZATION_CHART}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="organization/chart" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-BgPrimaryLight rounded-lg">
                <BuildingOffice2Icon className="w-8 h-8 text-bgPrimary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('organizations.chart') || 'Organisationsschema'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('organizations.chartDescription') || 'Visa och redigera organisationshierarki grafiskt'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('ADMIN_ORGANIZATIONS') && (
          <Link
            to={ROUTES.ADMIN_ORGANIZATIONS}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="organization/management" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-BgPrimaryLight rounded-lg">
                <UserGroupIcon className="w-8 h-8 text-bgPrimary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('admin.organizations') || 'Organisationshantering'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('organizations.managementDescription') || 'Skapa och hantera organisationer'}
                </p>
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

export default OrganizationDashboard;
